import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Rocket, Mail, CalendarCheck, Flag, UserPlus } from "lucide-react";
import { api, errMsg } from "../../lib/api";
import { PublicNav } from "../../components/PublicNav";
import { trackScan } from "./tracking";
import { RaceHeader, CandidatesCard, PollTrendCard, CtvReachCard, AiIdeasCard, FundraisingPreviewCard, WhatYouGet, Sources } from "./sections";
import { GuestModal, LeadModal } from "./modals";
import { Seo, raceEventJsonLd, breadcrumbJsonLd } from "../../components/Seo";

const CtaBar = ({ data, onGuest, onLead }) => {
  const navigate = useNavigate();
  return (
    <div className="sticky bottom-0 z-40 -mx-5 px-5 py-3 bg-app/90 backdrop-blur border-t border-white/10" data-testid="showcase-cta-bar">
      <div className="max-w-4xl mx-auto flex flex-col sm:flex-row gap-2 sm:items-center">
        <button onClick={onGuest} data-testid="cta-explore-workspace-btn"
          className="flex-1 rounded-xl bg-gradient-to-r from-amber-400 to-amber-600 text-on-accent font-bold py-3 text-sm inline-flex items-center justify-center gap-2 hover:opacity-90">
          <Rocket className="w-4 h-4" /> Explore the live workspace — no login
        </button>
        <button onClick={onLead} data-testid="cta-send-report-btn"
          className="flex-1 rounded-xl border border-amber-400/50 text-amber-200 font-semibold py-3 text-sm inline-flex items-center justify-center gap-2 hover:bg-amber-400/10">
          <Mail className="w-4 h-4" /> Send me this race report
        </button>
        <div className="flex items-center justify-center gap-4 text-xs text-slate-400 sm:pl-2">
          <button onClick={() => navigate("/request-demo")} className="inline-flex items-center gap-1 hover:text-white" data-testid="cta-book-demo"><CalendarCheck className="w-3.5 h-3.5" /> Book a demo</button>
          <button onClick={() => navigate(`/directory/race/${data.race.race_id}`)} className="inline-flex items-center gap-1 hover:text-white" data-testid="cta-claim-page"><Flag className="w-3.5 h-3.5" /> Claim your page</button>
          <button onClick={() => navigate("/signup")} className="inline-flex items-center gap-1 hover:text-white" data-testid="cta-sign-up"><UserPlus className="w-3.5 h-3.5" /> Sign up</button>
        </div>
      </div>
    </div>
  );
};

export default function RaceShowcase() {
  const { raceId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [modal, setModal] = useState(null);

  useEffect(() => {
    setData(null); setError("");
    api.get(`/public/scan/race/${raceId}`).then(({ data }) => setData(data)).catch((e) => setError(errMsg(e, "Race not found")));
    trackScan({ race_id: raceId });
  }, [raceId]);

  return (
    <div className="min-h-screen bg-app text-white" data-testid="race-showcase-page">
      <PublicNav compact />
      <main className="max-w-4xl mx-auto px-5 py-8 md:py-12 space-y-6">
        <button onClick={() => navigate("/scan")} className="text-xs text-slate-400 hover:text-amber-300 inline-flex items-center gap-1" data-testid="showcase-back-btn"><ArrowLeft className="w-3.5 h-3.5" /> Find a different race</button>
        {error && <div className="rounded-xl border border-rose-400/30 bg-rose-400/10 p-4 text-sm text-rose-200" data-testid="showcase-error">{error}</div>}
        {!data && !error && <div className="text-sm text-slate-400 flex items-center gap-2 py-16 justify-center" data-testid="showcase-loading"><Loader2 className="w-5 h-5 animate-spin" /> Pulling the race together…</div>}
        {data && (
          <>
            <Seo title={`${data.race.label} — 2026 race demo: candidates, FEC money, CTV reach`} description={`${data.candidates.length} declared candidates, live FEC fundraising, poll trend, modeled CTV voter reach and AI marketing plays for the 2026 ${data.race.label} race. ${data.days_until} days to Election Day.`} path={`/scan/race/${raceId}`}
              jsonLd={[raceEventJsonLd(data.race, `/scan/race/${raceId}`), breadcrumbJsonLd([{ name: "Home", path: "/" }, { name: "Find your race", path: "/scan" }, { name: data.race.label, path: `/scan/race/${raceId}` }])]} />
            <RaceHeader data={data} />
            <CandidatesCard data={data} />
            <div className="grid lg:grid-cols-2 gap-6">
              <PollTrendCard data={data} />
              <CtvReachCard est={data.ctv_estimate} />
            </div>
            <AiIdeasCard raceId={raceId} initial={data.ideas} />
            <FundraisingPreviewCard f={data.fundraising_sample} />
            <WhatYouGet items={data.what_you_get} />
            <Sources sources={data.sources} />
            <CtaBar data={data} onGuest={() => setModal("guest")} onLead={() => setModal("lead")} />
          </>
        )}
      </main>
      {modal === "guest" && data && <GuestModal data={data} onClose={() => setModal(null)} />}
      {modal === "lead" && data && <LeadModal data={data} onClose={() => setModal(null)} />}
    </div>
  );
}
